import React from 'react'
import { List } from 'immutable'
import { useWillUnmount } from 'rooks'
import { Observable } from 'rx'
import type { Campaign } from '@mobi/account/Areas/SignUp'
import { isCampaignRedeemableWithBettingType } from '@mobi/betslip/helpers/campaignHelper'
import {
  isFobSelection,
  isSameRaceMultiSelection,
  isStartingPriceSelection,
} from '@mobi/betslip/helpers/typeGuards'
import type { BettingType } from '@mobi/betslip/types'
import { closeQuickDeposit } from '@core/Areas/QuickDeposit/Store'
import { useHapticFeedbackOnBetSuccess } from '@core/Areas/Quickbet/hooks/useHapticFeedbackOnBetSuccess'
import { observeImmutable } from '@core/Components/HOCs'
import { useServerConfig } from '@core/Data/ServerConfig/useServerConfig'
import { keys } from '@core/Services/Analytics/keys'
import { trackEvent } from '@core/Services/Analytics/track'
import { state$ as userState$ } from '@core/State/UserAccount/userAccountDriver'
import { useAppDispatch } from '@core/Store/hooks'
import { QuickDeposit } from '../QuickDeposit'
import { BalanceInfoBar } from './Components/BalanceBar/BalanceBar'
import { BetInvestment } from './Components/BetInvestment/BetInvestment'
import { BetSelectionInfo } from './Components/BetSelectionInfo'
import { BonusBetList } from './Components/BonusBetList/BonusBetList'
import { BonusBetListItem } from './Components/BonusBetList/BonusBetListItem'
import { ButtonsContainer } from './Components/Buttons/ButtonsContainer'
import { QuickbetCampaigns } from './Components/Campaigns/QuickbetCampaigns'
import { HeaderStyled, QuickbetActionsStyled, QuickbetBodyStyled } from './Components/Common.styles'
import { QuickbetFormula } from './Components/Formula/QuickbetFormula'
import { KambiRewardContainer } from './Components/KambiReward'
import { QuickbetKeypad } from './Components/Keypad/Keypad'
import { MysteryQuickPickBonusToggleButton } from './Components/MysteryQuickPickBonusToggleButton/MysteryQuickPickBonusToggleButton'
import { Notifications } from './Components/Notifications/Notifications'
import { Receipt } from './Components/Receipt/Receipt'
import {
  SuperPickState,
  state$ as superPickState$,
} from './Components/SuperPick/Components/SuperPick/driver'
import { QuickbetSuperPick } from './Components/SuperPick/QuickbetSuperPick'
import { QuickbetState, state$ as quickbetState$ } from './driver'
import { register as registerAnalytics } from './helpers/analytics'
import { register as registerQuickbetLogging } from './helpers/logging'
import { useShareBetAcquisitionTracking } from './hooks/useSharedBetAcquisitionTracking'
import { SetBonusBet, ToggleBonusBetUsage, ToggleBonusCashUsage } from './signals'

type QuickbetProps = {
  accountBalance: number
  accountNumber: number
  activeCampaigns: List<Campaign> | null
  bettingType: BettingType | null
  bonusBetBalance: number
  bonusCashBalance: number
  isUsingBonusBet: boolean
  isUsingBonusCash: boolean
  notificationVisible: boolean
  isSuperPickEnabled: boolean
  shouldShowSuperPicks: boolean
  betPlaced?: boolean
  canProposeBet: boolean
  canConfirmBet: boolean
  insufficientFundsForBet: boolean
}

const state$ = Observable.combineLatest(
  quickbetState$,
  superPickState$,
  userState$,
  (quickbetStateRecord, superPickStateRecord, userState): QuickbetProps => {
    const quickbetState = quickbetStateRecord.toJS() as QuickbetState
    const superPickState = superPickStateRecord.toJS() as SuperPickState

    const shouldShowSuperPicks =
      isFobSelection(quickbetState.selection) &&
      !isSameRaceMultiSelection(quickbetState.selection) &&
      !isStartingPriceSelection(quickbetState.selection)

    const isSuperPickEnabled =
      shouldShowSuperPicks &&
      superPickState.specialOffers &&
      superPickState.specialOffers.length > 0 &&
      !!superPickState.selectedSuperPickSeq

    return {
      isSuperPickEnabled,
      shouldShowSuperPicks,
      bettingType: quickbetState.bettingType,
      isUsingBonusCash: quickbetState.isUsingBonusCash,
      isUsingBonusBet: quickbetState.isUsingBonusBet,
      accountNumber: userState.accountNumber || 0,
      accountBalance: userState.accountBalance || 0,
      bonusBetBalance: userState.bonusBetBalance || 0,
      bonusCashBalance: userState.bonusCashBalance || 0,
      activeCampaigns: userState.activeCampaigns,
      notificationVisible: !!quickbetState.notificationType,
      betPlaced: quickbetState.betPlaced,
      canProposeBet: quickbetState.canProposeBet,
      canConfirmBet: quickbetState.canConfirmBet,
      insufficientFundsForBet: quickbetState.insufficientFundsForBet,
    }
  }
)

export const Quickbet = observeImmutable(state$, ({ record }) => <QuickbetComponent {...record} />)

export const QuickbetComponent: React.FC<QuickbetProps> = ({
  accountBalance,
  accountNumber,
  activeCampaigns,
  betPlaced,
  bettingType,
  bonusBetBalance,
  bonusCashBalance,
  isUsingBonusBet,
  isUsingBonusCash,
  notificationVisible,
  isSuperPickEnabled,
  shouldShowSuperPicks,
  insufficientFundsForBet,
  canConfirmBet,
  canProposeBet,
}) => {
  const dispatch = useAppDispatch()

  const serverConfig = useServerConfig()
  useHapticFeedbackOnBetSuccess()
  useShareBetAcquisitionTracking()

  useWillUnmount(() => dispatch(closeQuickDeposit()))

  React.useEffect(() => {
    if (!serverConfig?.enableAnalytics) return
    const { dispose } = registerAnalytics()
    return dispose
  }, [serverConfig?.enableAnalytics])

  React.useEffect(() => registerQuickbetLogging().dispose, [])

  const isMysteryQuickPick = bettingType === 'mystery-quick-pick'

  const hasBonusCash = activeCampaigns?.some(
    campaign =>
      campaign.rewardType === 'BonusCashReward' &&
      (isCampaignRedeemableWithBettingType(campaign, 'fixed-odds-racing') ||
        isCampaignRedeemableWithBettingType(campaign, 'tote-racing') ||
        isMysteryQuickPick)
  )

  React.useEffect(() => {
    if (hasBonusCash) ToggleBonusCashUsage(false)
  }, [hasBonusCash])

  const mustUseBonusCash =
    bettingType !== 'fixed-odds-racing' &&
    bettingType !== 'tote-racing' &&
    bettingType !== 'kambi-sports' &&
    !isMysteryQuickPick

  const activeBonusCashCampaign = activeCampaigns
    ?.sort(x => x.rewardExpiry.valueOf())
    ?.find(x => x.rewardType === 'BonusCashReward')

  const showAvailableBonuses =
    (isUsingBonusBet && !notificationVisible) || (isMysteryQuickPick && isUsingBonusBet)

  const displayQuickDeposit =
    insufficientFundsForBet &&
    canConfirmBet &&
    quickDepositEnabledBettingTypes.includes(bettingType) &&
    !isUsingBonusBet

  const isKambiBetting = bettingType === 'kambi-sports' && (canConfirmBet || canProposeBet)

  return (
    <section>
      <HeaderStyled>
        <BalanceInfoBar />

        <BetSelectionInfo />
      </HeaderStyled>

      <BetInvestment />

      {isMysteryQuickPick && !betPlaced ? (
        <MysteryQuickPickBonusToggleButton
          handleClick={() => {
            ToggleBonusBetUsage()
            trackEvent(
              isUsingBonusBet
                ? keys.quickbetBonusCashButtonDeactivated
                : keys.quickbetBonusCashButtonActivated,
              { accountNumber, accountBalance, bonusBetBalance, bonusCashBalance }
            )
          }}
        />
      ) : null}

      <QuickbetFormula />

      {isUsingBonusCash || mustUseBonusCash ? <QuickbetCampaigns /> : null}

      {displayQuickDeposit ? (
        <QuickDeposit />
      ) : (
        <>
          <QuickbetBodyStyled id='quickbet-body'>
            {showAvailableBonuses ? (
              <>
                {activeBonusCashCampaign ? (
                  <BonusBetListItem
                    isBonusCash
                    id='bonus-cash'
                    name='Bonus Cash'
                    expiry={activeBonusCashCampaign.rewardExpiry}
                    amount={bonusCashBalance}
                    selected={isUsingBonusCash}
                    handleClick={() => {
                      if (isMysteryQuickPick) {
                        ToggleBonusCashUsage(true)
                      } else {
                        if (!isUsingBonusCash) {
                          // We're turning bonus cash on, so clear any selected bonus bet.
                          ToggleBonusBetUsage(false)
                          SetBonusBet(null)
                        }
                        ToggleBonusCashUsage()
                      }
                    }}
                  />
                ) : null}

                {!isMysteryQuickPick ? (
                  <BonusBetList
                    superPickEnabled={isSuperPickEnabled}
                    setBonusBet={bonusBet => {
                      SetBonusBet(bonusBet)
                      ToggleBonusCashUsage(false)
                    }}
                    bettingType={bettingType}
                  />
                ) : null}
              </>
            ) : null}

            {isKambiBetting && <KambiRewardContainer />}

            {!isUsingBonusBet && shouldShowSuperPicks ? <QuickbetSuperPick /> : null}

            {!isUsingBonusBet ? <QuickbetKeypad /> : null}

            <Notifications />

            <Receipt />
          </QuickbetBodyStyled>

          <QuickbetActionsStyled>
            <ButtonsContainer />
          </QuickbetActionsStyled>
        </>
      )}
    </section>
  )
}

// =============
// Local Helpers
// =============

const quickDepositEnabledBettingTypes: (BettingType | null)[] = [
  'tote-racing',
  'tote-sports-tipping',
  'fixed-odds-racing',
  'kambi-sports',
]
